import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { markPinVerified } from '../api/pinSession';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PIN_LEN = 6;

const T = {
  ar: {
    invTitle:    'أهلاً بك في جهبذ!',
    invSub:      'أدخل رمز الدعوة',
    invLabel:    'أدخل رمز الدعوة',
    invBtn:      'تحقق',
    setPinTitle: 'اختر رقمك السري',
    setPinSub:   'اختر ٦ أرقام ستستخدمها لتسجيل الدخول دائماً',
    confTitle:   'أكّد رقمك السري',
    confSub:     'أدخل نفس الأرقام مجدداً للتأكيد',
    loginTitle:  'أدخل رقمك السري',
    loginSub:    'أدخل رقمك السري المكوّن من ٦ أرقام',
    mismatch:    'الرقمان غير متطابقان، حاول مجدداً',
    wrongPin:    'رقم سري خاطئ، حاول مجدداً',
    invalid:     'كود غير صحيح أو منتهي الصلاحية',
    back:        '→ العودة للرئيسية',
    langBtn:     'EN',
    loading:     '...',
    firstTime:   'أول مرة؟ أدخل رمز الدعوة',
    backToLogin: 'لديك رقم سري؟ تسجيل الدخول',
  },
  en: {
    invTitle:    'Welcome to Jahbath!',
    invSub:      'Enter your invitation code',
    invLabel:    'Enter your invitation code',
    invBtn:      'Verify',
    setPinTitle: 'Choose your PIN',
    setPinSub:   "Pick 6 digits — you'll use them to log in every time",
    confTitle:   'Confirm your PIN',
    confSub:     'Enter the same digits again to confirm',
    loginTitle:  'Enter your PIN',
    loginSub:    'Enter your 6-digit secret PIN',
    mismatch:    "PINs don't match — try again",
    wrongPin:    'Incorrect PIN, try again',
    invalid:     'Invalid or expired code',
    back:        '← Back to Home',
    langBtn:     'ع',
    loading:     '...',
    firstTime:   'First time? Enter your invitation code',
    backToLogin: 'Have a PIN? Log in',
  },
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .sa-orb1 {
    position:fixed; top:-140px; right:-140px; width:500px; height:500px;
    background:radial-gradient(circle,rgba(5,150,105,.09) 0%,transparent 70%);
    border-radius:50%; pointer-events:none; z-index:0;
  }
  .sa-orb2 {
    position:fixed; bottom:-100px; left:-100px; width:400px; height:400px;
    background:radial-gradient(circle,rgba(217,119,6,.07) 0%,transparent 70%);
    border-radius:50%; pointer-events:none; z-index:0;
  }

  @keyframes sa-slideUp {
    from { opacity:0; transform:translateY(28px) scale(.97); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes sa-floatHat {
    0%,100% { transform:translateY(0); }
    50%     { transform:translateY(-6px); }
  }
  @keyframes sa-shake {
    0%,100% { transform:translateX(0); }
    20%     { transform:translateX(-7px); }
    40%     { transform:translateX(7px); }
    60%     { transform:translateX(-4px); }
    80%     { transform:translateX(4px); }
  }
  @keyframes sa-ripple {
    0%   { box-shadow:0 0 0 0 rgba(5,150,105,.35); }
    100% { box-shadow:0 0 0 14px rgba(5,150,105,0); }
  }

  .sa-lang-btn {
    position:fixed; top:20px; left:20px; z-index:100;
    background:white; border:2px solid #E5E7EB;
    color:#059669; font-family:"Sora",sans-serif;
    font-weight:700; font-size:.8rem; padding:5px 14px;
    border-radius:20px; cursor:pointer; transition:all .2s;
    box-shadow:0 3px 0 #E5E7EB, 0 4px 10px rgba(0,0,0,.06);
  }
  .sa-lang-btn:hover { background:#F0FDF4; border-color:#059669; box-shadow:0 3px 0 #D1FAE5; transform:translateY(-1px); }

  .sa-card {
    background:rgba(255,255,255,.72); border-radius:32px; padding:44px 40px;
    width:100%; max-width:420px;
    box-shadow:0 8px 48px rgba(0,0,0,.07), 0 1px 0 rgba(255,255,255,.98) inset;
    border:1px solid rgba(255,255,255,.92);
    backdrop-filter:blur(32px) saturate(2.2);
    -webkit-backdrop-filter:blur(32px) saturate(2.2);
    position:relative; z-index:1;
    animation:sa-slideUp .5s cubic-bezier(.22,.68,0,1.2) both;
  }
  .sa-hat {
    height:60px; width:auto; margin-bottom:8px;
    filter:drop-shadow(0 6px 16px rgba(5,150,105,.2));
    animation:sa-floatHat 3.5s ease-in-out infinite;
  }

  .sa-digits {
    display:flex; justify-content:center; gap:10px;
    direction:ltr; margin-bottom:24px;
  }
  .sa-digit-box {
    width:44px; height:54px; border-radius:16px;
    border:2px solid #E5E7EB; background:white;
    display:flex; align-items:center; justify-content:center;
    font-family:"Sora",sans-serif; font-size:1.4rem; font-weight:800;
    color:#064E3B; transition:border-color .15s, box-shadow .15s; flex-shrink:0;
    box-shadow:0 3px 0 #E5E7EB;
  }
  .sa-digit-box.filled {
    border-color:#059669; background:#F0FDF4;
    box-shadow:0 3px 0 rgba(5,150,105,.2);
  }
  .sa-digit-box.active {
    border-color:#059669;
    box-shadow:0 3px 0 rgba(5,150,105,.2), 0 0 0 4px rgba(5,150,105,.12);
  }
  .sa-digit-box.error {
    border-color:#EF4444; background:#FEF2F2;
    box-shadow:0 3px 0 rgba(239,68,68,.2);
    animation:sa-shake .4s ease;
  }

  .sa-numpad {
    display:grid; grid-template-columns:repeat(3,1fr); gap:10px;
    direction:ltr; margin-top:4px;
  }
  .sa-key {
    background:white; border:2px solid #E5E7EB;
    border-radius:18px; padding:0;
    font-family:"Sora",sans-serif; font-size:1.3rem;
    font-weight:700; color:#064E3B;
    cursor:pointer; transition:transform .08s, box-shadow .08s, border-color .08s;
    display:flex; align-items:center; justify-content:center;
    min-height:64px; box-shadow:0 4px 0 #E5E7EB, 0 6px 12px rgba(0,0,0,.04);
    user-select:none; -webkit-user-select:none;
  }
  .sa-key:hover:not(.sa-key-empty):not(:disabled) {
    background:#F0FDF4; border-color:#059669;
    transform:translateY(-3px); box-shadow:0 4px 0 #D1D5DB, 0 10px 20px rgba(5,150,105,.14);
  }
  .sa-key:active:not(.sa-key-empty):not(:disabled) {
    transform:translateY(3px); background:#D1FAE5;
    box-shadow:0 1px 0 #E5E7EB;
    animation:sa-ripple .4s ease-out;
  }
  .sa-key.sa-key-empty {
    background:transparent; border:none; box-shadow:none;
    cursor:default; pointer-events:none;
  }
  .sa-key.sa-key-del { font-size:1.1rem; color:#6B7280; }
  .sa-key.sa-key-del:hover:not(:disabled) {
    color:#EF4444; border-color:#fca5a5; background:#FEF2F2;
    box-shadow:0 4px 0 #FECACA;
  }
  .sa-key:disabled { opacity:.4; cursor:not-allowed; }

  .sa-code-input {
    width:100%; padding:14px 16px;
    border:2px solid #E5E7EB; border-radius:18px;
    font-family:"Sora",sans-serif; font-size:1rem; font-weight:700;
    color:#064E3B; outline:none; text-align:center; letter-spacing:.1em;
    transition:border-color .2s; box-shadow:0 3px 0 #E5E7EB;
  }
  .sa-code-input:focus { border-color:#059669; box-shadow:0 3px 0 rgba(5,150,105,.2), 0 0 0 4px rgba(5,150,105,.12); }
  .sa-code-input.error { border-color:#EF4444; background:#FEF2F2; }

  .sa-btn-primary {
    width:100%; padding:15px;
    background:#D1FAE5;
    border:2px solid #6EE7B7; border-radius:18px;
    font-family:"Sora",sans-serif; font-size:1rem; font-weight:900;
    color:#065F46; cursor:pointer; margin-top:16px;
    transition:all .2s; box-shadow:0 5px 0 #6EE7B7, 0 8px 16px rgba(5,150,105,.15);
  }
  .sa-btn-primary:hover:not(:disabled) {
    transform:translateY(-3px); box-shadow:0 5px 0 #6EE7B7, 0 14px 24px rgba(5,150,105,.2);
  }
  .sa-btn-primary:active:not(:disabled) { transform:translateY(4px); box-shadow:0 1px 0 #6EE7B7; }
  .sa-btn-primary:disabled { opacity:.5; cursor:not-allowed; }

  .sa-err     { color:#EF4444; font-size:.84rem; font-weight:600; text-align:center; min-height:22px; margin-bottom:12px; }
  .sa-success { color:#059669; font-size:.84rem; font-weight:700; text-align:center; min-height:22px; margin-bottom:12px; }
  .sa-back { display:block; text-align:center; font-size:.82rem; color:#6B7280; cursor:pointer; margin-top:20px; font-weight:600; transition:color .2s; text-decoration:none; }
  .sa-back:hover { color:#059669; }
  .sa-first-time { display:block; text-align:center; margin-top:20px; padding:11px 0; border-radius:14px; background:rgba(5,150,105,.07); border:1.5px solid rgba(5,150,105,.2); font-size:13px; font-weight:800; color:#059669; cursor:pointer; transition:all .25s; text-decoration:none; }
  .sa-first-time:hover { background:rgba(5,150,105,.13); transform:translateY(-2px); }

  @media (max-width:480px) {
    .sa-card { padding:30px 18px; }
    .sa-digit-box { width:38px; height:48px; font-size:1.2rem; }
    .sa-key { min-height:56px; font-size:1.2rem; }
  }
  @media (min-width:481px) and (max-width:767px) {
    .sa-card { max-width:460px; }
    .sa-numpad { max-width:340px; }
  }
`;

export default function StudentAuth() {
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();

  const [screen,     setScreen]     = useState(null);
  const [pin,        setPin]        = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [firstPin,   setFirstPin]   = useState('');
  const [errorMsg,   setErrorMsg]   = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [dotsError,  setDotsError]  = useState(false);
  const [loading,    setLoading]    = useState(false);

  const canvasRef = useRef(null);

  const t   = T[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  // ── Determine screen on mount ─────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      setScreen('login');
      return;
    }
    try {
      const data = JSON.parse(localStorage.getItem('student_data') || '{}');
      setScreen(data.pin_set ? 'login' : 'setPin');
    } catch {
      setScreen('login');
    }
  }, []);

  // ── Auto-advance when PIN complete ────────────────────────────────────────
  useEffect(() => {
    if (screen === 'invitation' && pin.length === PIN_LEN) {
      handleVerifyCode(pin);
    }
    if (screen === 'setPin' && pin.length === PIN_LEN) {
      setTimeout(() => {
        setFirstPin(pin);
        setPin('');
        setScreen('confirmPin');
      }, 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, screen]);

  useEffect(() => {
    if (screen === 'confirmPin' && confirmPin.length === PIN_LEN) {
      setTimeout(() => handleConfirmPin(), 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmPin, screen]);

  useEffect(() => {
    if (screen === 'login' && pin.length === PIN_LEN) {
      setTimeout(() => handleLogin(), 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, screen]);

  // ── Three.js background ───────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.26, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  // ── Keyboard PIN support ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (screen === 'invitation' || screen === 'setPin' || screen === 'confirmPin' || screen === 'login') {
        if (e.key >= '0' && e.key <= '9') pressNum(e.key);
        if (e.key === 'Backspace') deleteLast();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, pin, confirmPin]);

  // ── Shake + clear helper ──────────────────────────────────────────────────
  const shakeAndClear = (msg, backToSetPin = false) => {
    setErrorMsg(msg);
    setDotsError(true);
    setTimeout(() => {
      setDotsError(false);
      if (backToSetPin) {
        setConfirmPin('');
        setPin('');
        setScreen('setPin');
      } else if (screen === 'confirmPin') {
        setConfirmPin('');
      } else {
        setPin('');
      }
    }, 650);
  };

  // ── Numpad ────────────────────────────────────────────────────────────────
  const pressNum = (n) => {
    if (loading) return;
    setErrorMsg('');
    if (screen === 'confirmPin') {
      setConfirmPin(prev => prev.length < PIN_LEN ? prev + n : prev);
    } else {
      setPin(prev => prev.length < PIN_LEN ? prev + n : prev);
    }
  };

  const deleteLast = () => {
    if (loading) return;
    setErrorMsg('');
    if (screen === 'confirmPin') setConfirmPin(prev => prev.slice(0, -1));
    else                         setPin(prev => prev.slice(0, -1));
  };

  // ── Screen 1: verify invitation ───────────────────────────────────────────
  const handleVerifyCode = async (codeValue) => {
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const res  = await fetch(`${API}/auth/student/verify-invitation`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ code: codeValue }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('student_token', data.token);
        localStorage.setItem('student_data',  JSON.stringify(data.student));
        localStorage.setItem('jahbath_student_grade', String(data.student.grade || 1));
        localStorage.setItem('jahbath_student_name',  data.student.first_name || '');
        setPin('');
        setSuccessMsg(lang === 'ar' ? '✅ الكود صحيح!' : '✅ Code accepted!');
        setTimeout(() => {
          setSuccessMsg('');
          setLoading(false);
          setScreen(data.student.pin_set ? 'login' : 'setPin');
        }, 1500);
        return;
      }
      const errCode = data.errors?.[0]?.code || '';
      if (errCode === 'JAH_ERR_CODE_EXPIRED')  setErrorMsg(t.invalid + (lang === 'ar' ? ' (منتهية الصلاحية)' : ' (expired)'));
      else if (errCode === 'JAH_ERR_CODE_USED') setErrorMsg(t.invalid + (lang === 'ar' ? ' (مستخدم)' : ' (used)'));
      else                                       setErrorMsg(t.invalid);
      setDotsError(true);
      setTimeout(() => { setDotsError(false); setPin(''); }, 650);
    } catch {
      setErrorMsg(t.invalid);
      setDotsError(true);
      setTimeout(() => { setDotsError(false); setPin(''); }, 650);
    }
    setLoading(false);
  };

  // ── Screen 3: confirm PIN ────────────────────────────────────────────────
  const handleConfirmPin = () => {
    if (confirmPin !== firstPin) {
      shakeAndClear(t.mismatch, true);
      return;
    }
    callSetPin(firstPin);
  };

  const callSetPin = async (pinValue) => {
    setLoading(true);
    const token = localStorage.getItem('student_token') || '';
    try {
      const res  = await fetch(`${API}/auth/student/set-pin`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ pin: pinValue }),
      });
      const data = await res.json();
      if (data.success) {
        try {
          const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
          sd.pin_set = true;
          localStorage.setItem('student_data', JSON.stringify(sd));
        } catch {}
        markPinVerified();
        navigate('/student/onboarding');
        return;
      }
      shakeAndClear(data.errors?.[0]?.message || t.wrongPin);
    } catch {
      shakeAndClear(t.wrongPin);
    } finally {
      setLoading(false);
    }
  };

  // ── Screen 4: login ───────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/student/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('student_token', data.token);
        localStorage.setItem('student_data',  JSON.stringify(data.student));
        localStorage.setItem('jahbath_student_grade', String(data.student.grade || 1));
        localStorage.setItem('jahbath_student_name',  data.student.first_name || '');
        if (data.student?.language) {
          localStorage.setItem('jahbath_student_lang', data.student.language);
        }
        markPinVerified();
        navigate('/student/dashboard');
        return;
      }
      shakeAndClear(t.wrongPin);
    } catch {
      shakeAndClear(t.wrongPin);
    } finally {
      setLoading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const activePin = screen === 'confirmPin' ? confirmPin : pin;

  const renderDigits = () => {
    const showClear = screen === 'invitation';
    return (
      <div className="sa-digits">
        {Array.from({ length: PIN_LEN }, (_, i) => {
          let cls = 'sa-digit-box';
          if (dotsError)               cls += ' error';
          else if (i < activePin.length)  cls += ' filled';
          else if (i === activePin.length) cls += ' active';
          const char = i < activePin.length ? (showClear ? activePin[i] : '●') : '';
          return <div key={i} className={cls}>{char}</div>;
        })}
      </div>
    );
  };

  const renderNumpad = () => (
    <div className="sa-numpad">
      {['1','2','3','4','5','6','7','8','9'].map(n => (
        <button key={n} className="sa-key" onClick={() => pressNum(n)} disabled={loading}>{n}</button>
      ))}
      <button className="sa-key sa-key-empty" />
      <button className="sa-key" onClick={() => pressNum('0')} disabled={loading}>0</button>
      <button className="sa-key sa-key-del" onClick={deleteLast} disabled={loading}>⌫</button>
    </div>
  );

  return (
    <div style={{
      fontFamily:     '"Nunito", sans-serif',
      background:     'transparent',
      minHeight:      '100vh',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        24,
      overflowX:      'hidden',
      position:       'relative',
      direction:      dir,
    }}>
      <canvas ref={canvasRef} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />
      <style>{CSS}</style>
      <div className="sa-orb1" />
      <div className="sa-orb2" />

      <button className="sa-lang-btn" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
        {t.langBtn}
      </button>

      <div style={{position:'relative',zIndex:10,visibility:screen?'visible':'hidden'}}>
      <div className="sa-card">

        {/* Logo */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:28 }}>
          <img src="/jahbath-hat.png" alt="Jahbath" className="sa-hat" />
          <div style={{ fontFamily:'"Sora",sans-serif', fontSize:'1.7rem', fontWeight:800, color:'#1E3A5F', letterSpacing:'-.02em' }}>
            Jah<span style={{ color:'#059669' }}>bath</span>
          </div>
        </div>

        {/* ── Screen 1: Invitation ────────────────────────────────────── */}
        {screen === 'invitation' && (
          <>
            <div style={{ fontFamily:'"Sora",sans-serif', fontSize:'1.15rem', fontWeight:800, color:'#064E3B', textAlign:'center', marginBottom:6 }}>
              {t.invTitle}
            </div>
            <div style={{ fontSize:'.84rem', color:'#6B7280', textAlign:'center', marginBottom:24, fontWeight:600 }}>
              {t.invLabel}
            </div>
            {renderDigits()}
            {successMsg
              ? <div className="sa-success">{successMsg}</div>
              : <div className="sa-err">{errorMsg}</div>
            }
            {renderNumpad()}
            <span
              className="sa-back"
              onClick={() => { setScreen('login'); setPin(''); }}
            >
              {t.backToLogin}
            </span>
          </>
        )}

        {/* ── Screens 2 & 3: Set + Confirm PIN ───────────────────────── */}
        {(screen === 'setPin' || screen === 'confirmPin') && (
          <>
            <div style={{ fontFamily:'"Sora",sans-serif', fontSize:'1.1rem', fontWeight:800, color:'#064E3B', textAlign:'center', marginBottom:6 }}>
              {screen === 'setPin' ? t.setPinTitle : t.confTitle}
            </div>
            <div style={{ fontSize:'.82rem', color:'#6B7280', textAlign:'center', marginBottom:24, fontWeight:600 }}>
              {screen === 'setPin' ? t.setPinSub : t.confSub}
            </div>
            {renderDigits()}
            <div className="sa-err">{errorMsg}</div>
            {renderNumpad()}
          </>
        )}

        {/* ── Screen 4: Login ─────────────────────────────────────────── */}
        {screen === 'login' && (
          <>
            <div style={{ fontFamily:'"Sora",sans-serif', fontSize:'1.1rem', fontWeight:800, color:'#064E3B', textAlign:'center', marginBottom:6 }}>
              {t.loginTitle}
            </div>
            <div style={{ fontSize:'.82rem', color:'#6B7280', textAlign:'center', marginBottom:24, fontWeight:600 }}>
              {t.loginSub}
            </div>
            {renderDigits()}
            <div className="sa-err">{errorMsg}</div>
            {renderNumpad()}
            <span
              className="sa-first-time"
              onClick={() => { setScreen('invitation'); setPin(''); setErrorMsg(''); }}
            >
              {t.firstTime}
            </span>
          </>
        )}

        <a href="/choose-role" style={{
          position:'fixed', top:20, right:20,
          display:'inline-flex', alignItems:'center', gap:6,
          padding:'7px 16px',
          background:'rgba(255,255,255,.8)',
          backdropFilter:'blur(16px)',
          WebkitBackdropFilter:'blur(16px)',
          border:'1px solid rgba(0,0,0,.09)',
          borderRadius:100,
          fontSize:12, fontWeight:700, color:'#6B7280',
          textDecoration:'none', zIndex:200,
          transition:'all .2s',
        }}>{t.back}</a>
      </div>
      </div>
    </div>
  );
}
